import { defineMessages } from 'react-intl';

export const templateEditorMessages = defineMessages({
  // Template form
  templateName: {
    id: 'templateEditor.templateName',
    defaultMessage: 'Template Name',
  },
  language: {
    id: 'templateEditor.language',
    defaultMessage: 'Language',
  },
  status: {
    id: 'templateEditor.status',
    defaultMessage: 'Status',
  },
  save: {
    id: 'templateEditor.save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'templateEditor.cancel',
    defaultMessage: 'Cancel',
  },
  unsavedChangesTitle: {
    id: 'templateEditor.unsavedChangesTitle',
    defaultMessage: 'Unsaved changes',
  },
  unsavedChangesDescription: {
    id: 'templateEditor.unsavedChangesDescription',
    defaultMessage: 'You have unsaved changes. Are you sure you want to leave?',
  },
  unsavedChangesLeave: {
    id: 'templateEditor.unsavedChangesLeave',
    defaultMessage: 'Leave',
  },
  unsavedChangesStay: {
    id: 'templateEditor.unsavedChangesStay',
    defaultMessage: 'Stay',
  },
  createTemplate: {
    id: 'templateEditor.createTemplate',
    defaultMessage: 'Create Template',
  },
  editTemplate: {
    id: 'templateEditor.editTemplate',
    defaultMessage: 'Edit Template',
  },
  systemBadge: {
    id: 'templateEditor.systemBadge',
    defaultMessage: 'System',
  },

  // Sections
  addSection: {
    id: 'templateEditor.addSection',
    defaultMessage: 'Add Section',
  },
  sectionTitle: {
    id: 'templateEditor.sectionTitle',
    defaultMessage: 'Section title',
  },
  sectionOptional: {
    id: 'templateEditor.sectionOptional',
    defaultMessage: 'Optional',
  },
  deleteSection: {
    id: 'templateEditor.deleteSection',
    defaultMessage: 'Delete Section',
  },

  // Options
  addStrictOption: {
    id: 'templateEditor.addStrictOption',
    defaultMessage: 'Add Option',
  },
  optionPlaceholder: {
    id: 'templateEditor.optionPlaceholder',
    defaultMessage: 'Option text...',
  },
  unsupportedOptionsRemovedNotice: {
    id: 'templateEditor.unsupportedOptionsRemovedNotice',
    defaultMessage:
      'Options and fields were removed: only Protocol templates support them.',
  },
  deleteOption: {
    id: 'templateEditor.deleteOption',
    defaultMessage: 'Delete Option',
  },

  // Slots
  addSlot: {
    id: 'templateEditor.addSlot',
    defaultMessage: 'Add Field',
  },
  slotValues: {
    id: 'templateEditor.slotValues',
    defaultMessage: 'Predefined Values',
  },
  slotValuesPlaceholder: {
    id: 'templateEditor.slotValuesPlaceholder',
    defaultMessage: 'One value per line',
  },
  editSlot: {
    id: 'templateEditor.editSlot',
    defaultMessage: 'Edit Field',
  },
  slotAiToggle: {
    id: 'templateEditor.slotAiToggle',
    defaultMessage: 'Filled by AI',
  },
  slotAiDescription: {
    id: 'templateEditor.slotAiDescription',
    defaultMessage:
      'This field will be filled by AI from the transcript. No predefined values needed.',
  },
  slotAiInstructions: {
    id: 'templateEditor.slotAiInstructions',
    defaultMessage: 'AI instructions',
  },
  slotAiInstructionsPlaceholder: {
    id: 'templateEditor.slotAiInstructionsPlaceholder',
    defaultMessage:
      'e.g. Use the tooth numbering system mentioned by the doctor',
  },

  // Header
  importButton: {
    id: 'templateEditor.importButton',
    defaultMessage: 'Import',
  },
  exportButton: {
    id: 'templateEditor.exportButton',
    defaultMessage: 'Export',
  },
  importError: {
    id: 'templateEditor.importError',
    defaultMessage: 'Failed to parse imported file',
  },
  importModalTitle: {
    id: 'templateEditor.importModalTitle',
    defaultMessage: 'Import or analyse a template',
  },
  importSegmentFile: {
    id: 'templateEditor.importSegmentFile',
    defaultMessage: 'Upload .json',
  },
  importSegmentBlank: {
    id: 'templateEditor.importSegmentBlank',
    defaultMessage: 'Analyse template',
  },
  importSegmentFilled: {
    id: 'templateEditor.importSegmentFilled',
    defaultMessage: 'Analyse record',
  },
  importUploadFile: {
    id: 'templateEditor.importUploadFile',
    defaultMessage: 'Upload file',
  },
  importAnalyse: {
    id: 'templateEditor.importAnalyse',
    defaultMessage: 'Analyse',
  },
  importPlaceholderBlank: {
    id: 'templateEditor.importPlaceholderBlank',
    defaultMessage: 'Paste the blank form',
  },
  importPlaceholderFilled: {
    id: 'templateEditor.importPlaceholderFilled',
    defaultMessage: 'Paste the filled-in record',
  },
  importDropJson: {
    id: 'templateEditor.importDropJson',
    defaultMessage: 'Or drag and drop a .json file here',
  },
  importDropText: {
    id: 'templateEditor.importDropText',
    defaultMessage: 'Or drag and drop a .txt or .md file here',
  },
  importNoSections: {
    id: 'templateEditor.importNoSections',
    defaultMessage:
      'No sections could be recognized. It helps when headings start on a new line and field names end with a colon.',
  },
  importInvalidJson: {
    id: 'templateEditor.importInvalidJson',
    defaultMessage: 'The file content is not a valid template JSON',
  },
  importStrategyPrompt: {
    id: 'templateEditor.importStrategyPrompt',
    defaultMessage:
      'The template already has sections — choose what to do with the result',
  },
  importReplaceAll: {
    id: 'templateEditor.importReplaceAll',
    defaultMessage: 'Replace all sections',
  },
  importAddToExisting: {
    id: 'templateEditor.importAddToExisting',
    defaultMessage: 'Add to existing sections',
  },
  importUnsupportedFile: {
    id: 'templateEditor.importUnsupportedFile',
    defaultMessage: 'Import supports .txt, .md and .json files',
  },
  importEmptyFile: {
    id: 'templateEditor.importEmptyFile',
    defaultMessage: 'Imported file is empty',
  },

  // Empty state
  emptyEditorTitle: {
    id: 'templateEditor.emptyEditorTitle',
    defaultMessage: 'Add sections to define the template structure',
  },
  addSectionButton: {
    id: 'templateEditor.addSectionButton',
    defaultMessage: 'Add section',
  },

  withoutSpecialty: {
    id: 'templateEditor.withoutSpecialty',
    defaultMessage: 'Without specialty',
  },

  // Settings popup
  metaFormTitle: {
    id: 'templateEditor.metaFormTitle',
    defaultMessage: 'Template details',
  },

  // Parse dialog
  saveSuccess: {
    id: 'templateEditor.saveSuccess',
    defaultMessage: 'Template saved successfully',
  },
  saveError: {
    id: 'templateEditor.saveError',
    defaultMessage: 'Failed to save template',
  },
  seriesMovedError: {
    id: 'templateEditor.seriesMovedError',
    defaultMessage:
      'This template was saved elsewhere after you opened it, so your changes were not saved over that version. Reload the page to see the current version and make your changes again.',
  },
  seriesBusyError: {
    id: 'templateEditor.seriesBusyError',
    defaultMessage:
      'Another save of this template is still under way. Your changes are kept; save again in a moment.',
  },
  validationNameRequired: {
    id: 'templateEditor.validationNameRequired',
    defaultMessage: 'Template name is required',
  },
  validationCodeRequired: {
    id: 'templateEditor.validationCodeRequired',
    defaultMessage: 'Template code is required',
  },
  validationTabTitleRequired: {
    id: 'templateEditor.validationTabTitleRequired',
    defaultMessage: 'Tab title is required',
  },
  validationSectionTitleRequired: {
    id: 'templateEditor.validationSectionTitleRequired',
    defaultMessage: 'Each section must have a title',
  },
  validationSectionCodeRequired: {
    id: 'templateEditor.validationSectionCodeRequired',
    defaultMessage: 'Each section must have a code',
  },
  validationOptionTextRequired: {
    id: 'templateEditor.validationOptionTextRequired',
    defaultMessage: 'Option text cannot be empty',
  },
  validationSectionNeedsOption: {
    id: 'templateEditor.validationSectionNeedsOption',
    defaultMessage: 'Strict sections must contain at least one option',
  },
  validationSlotPipeForbidden: {
    id: 'templateEditor.validationSlotPipeForbidden',
    defaultMessage: 'Slot values cannot contain the "|" character',
  },
  checklistButton: {
    id: 'templateEditor.checklistButton',
    defaultMessage: 'Checklist',
  },
  undoButton: {
    id: 'templateEditor.undoButton',
    defaultMessage: 'Undo',
  },
  redoButton: {
    id: 'templateEditor.redoButton',
    defaultMessage: 'Redo',
  },
  generationAlways: {
    id: 'templateEditor.generationAlways',
    defaultMessage: 'Always',
  },
  generationOnDemand: {
    id: 'templateEditor.generationOnDemand',
    defaultMessage: 'On demand',
  },
  protocolCannotBeAlways: {
    id: 'encounterSchema.protocolCannotBeAlways',
    defaultMessage: 'Protocol templates cannot be marked as always generated',
  },
  duplicateTemplateNameError: {
    id: 'templateEditor.duplicateTemplateNameError',
    defaultMessage: 'A template with this name already exists',
  },
  duplicateTemplateCodeError: {
    id: 'templateEditor.duplicateTemplateCodeError',
    defaultMessage:
      'This system code is already used for the selected language',
  },

  // Source document dialog
  sourceDocumentTitle: {
    id: 'templateEditor.sourceDocumentTitle',
    defaultMessage: 'Source Document',
  },
  sourceDocumentPlaceholder: {
    id: 'templateEditor.sourceDocumentPlaceholder',
    defaultMessage: 'Paste the original document text here...',
  },

  // System template clone
  cloneToOrganization: {
    id: 'templateEditor.cloneToOrganization',
    defaultMessage: 'Clone to Organization',
  },
  cloneSuccess: {
    id: 'templateEditor.cloneSuccess',
    defaultMessage: 'Template cloned successfully',
  },
  cloneError: {
    id: 'templateEditor.cloneError',
    defaultMessage: 'Failed to clone template',
  },
  systemTemplateReadOnly: {
    id: 'templateEditor.systemTemplateReadOnly',
    defaultMessage:
      'System templates are read-only. Clone to your organization to customize.',
  },

  // Constants labels
  statusDraftLabel: {
    id: 'templateEditor.statusDraftLabel',
    defaultMessage: 'Draft',
  },
  statusCurrentLabel: {
    id: 'templateEditor.statusCurrentLabel',
    defaultMessage: 'Current',
  },
  docTypeEvaluation: {
    id: 'templateEditor.docTypeEvaluation',
    defaultMessage: 'Evaluation',
  },
  docTypeSummary: {
    id: 'templateEditor.docTypeSummary',
    defaultMessage: 'Summary',
  },
  docTypeProtocol: {
    id: 'templateEditor.docTypeProtocol',
    defaultMessage: 'Protocol',
  },
  docTypeLiteratureReview: {
    id: 'templateEditor.docTypeLiteratureReview',
    defaultMessage: 'Literature Review',
  },
  docTypeLetter: {
    id: 'templateEditor.docTypeLetter',
    defaultMessage: 'Letter',
  },
  docTypeFollowUpLetter: {
    id: 'templateEditor.docTypeFollowUpLetter',
    defaultMessage: 'Follow-up letter',
  },

  // Header, details modal and AI instructions
  allTemplatesLink: {
    id: 'templateEditor.allTemplatesLink',
    defaultMessage: 'All templates',
  },
  editTemplateDetails: {
    id: 'templateEditor.editTemplateDetails',
    defaultMessage: 'Edit template details',
  },
  specialties: {
    id: 'templateEditor.specialties',
    defaultMessage: 'Specialties',
  },
  specialtiesPlaceholder: {
    id: 'templateEditor.specialtiesPlaceholder',
    defaultMessage: 'Select specialties…',
  },
  noSpecialtiesAvailable: {
    id: 'templateEditor.noSpecialtiesAvailable',
    defaultMessage: 'No specialties yet',
  },
  specialtiesHint: {
    id: 'templateEditor.specialtiesHint',
    defaultMessage:
      'Everyone holding one of these specialties will be offered this template. Leave empty to keep it clinic-wide.',
  },
  removeSpecialty: {
    id: 'templateEditor.removeSpecialty',
    defaultMessage: 'Remove specialty',
  },
  metaSpecialtiesLabel: {
    id: 'templateEditor.metaSpecialtiesLabel',
    defaultMessage: 'Specialties:',
  },
  docTypeProtocolDescription: {
    id: 'templateEditor.docTypeProtocolDescription',
    defaultMessage:
      'Step-by-step record of what was performed during the visit — the clinical operative note.',
  },
  docTypeSummaryDescription: {
    id: 'templateEditor.docTypeSummaryDescription',
    defaultMessage:
      'Condensed recap of the consultation: complaints, findings and the agreed plan.',
  },
  docTypeEvaluationDescription: {
    id: 'templateEditor.docTypeEvaluationDescription',
    defaultMessage:
      'Structured assessment with diagnoses and reasoning, for review or second opinion.',
  },
  docTypeFollowUpLetterDescription: {
    id: 'templateEditor.docTypeFollowUpLetterDescription',
    defaultMessage:
      'Correspondence addressed to a patient or referring colleague.',
  },
  docTypeLetterDescription: {
    id: 'templateEditor.docTypeLetterDescription',
    defaultMessage:
      'A letter written from the encounter, such as the treatment summary for the patient.',
  },
  docTypeLiteratureReviewDescription: {
    id: 'templateEditor.docTypeLiteratureReviewDescription',
    defaultMessage: 'Evidence summary supporting the proposed treatment.',
  },
  aiInstructionsTitle: {
    id: 'templateEditor.aiInstructionsTitle',
    defaultMessage: 'AI Instructions',
  },
  aiInstructionsForSection: {
    id: 'templateEditor.aiInstructionsForSection',
    defaultMessage: 'Applies to Section {number}',
  },
  aiInstructionsForOption: {
    id: 'templateEditor.aiInstructionsForOption',
    defaultMessage: 'Applies to Option {number}',
  },
  aiOptionRowText: {
    id: 'templateEditor.aiOptionRowText',
    defaultMessage: 'This field will be populated by AI',
  },
  addAiOption: {
    id: 'templateEditor.addAiOption',
    defaultMessage: 'AI Option',
  },
  deleteAiOption: {
    id: 'templateEditor.deleteAiOption',
    defaultMessage: 'Delete AI option',
  },
  dragHandle: {
    id: 'templateEditor.dragHandle',
    defaultMessage: 'Drag to reorder',
  },
  specialtyAttachError: {
    id: 'templateEditor.specialtyAttachError',
    defaultMessage:
      'Template was saved, but updating its specialties failed. Save again to retry.',
  },
});
